import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { Link, useLocation } from 'react-router-dom';
import CustomButton from '../components/CustomButton';
import { useNavigate } from 'react-router-dom';
import getTheme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import logo from '../assets/logo/Brix-Logo.png';

const Header = (): JSX.Element => {
  const location = useLocation();
  const theme = getTheme();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // For responsive menu

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('token');
    setIsLoggedIn(!!isAuthenticated);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    navigate('/login');
  };

  const featuresHref = location.pathname === '/' ? '#features' : '/features';
  const subscriptionHref = location.pathname === '/' ? '#subscription' : '/subscription';
  const contactHref = location.pathname === '/' ? '#contact' : '/contact';

  const scrollToSection = (hash: string) => {
    const section = document.querySelector(hash);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLinkClick = (href: string) => {
    setDrawerOpen(false);
    if (href.startsWith('#')) {
      scrollToSection(href);
    } else {
      navigate(href);
    }
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        color='transparent'
        position='sticky'
        sx={{
          border: 0,
          paddingY: '5px',
          padding: '7px 0',
          marginBottom: '5px',
          top: 'auto',
          boxShadow:
            '0 4px 18px 0px rgba(0, 0, 0, 0.12), 0 7px 10px -5px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Toolbar sx={{ minHeight: '80px', alignItems: 'center' }}>
          <Link to='/' style={{ textDecoration: 'none', marginTop: '7px', marginLeft: '-11px' }}>
            <img src={logo} alt='logo' height={60} width={185} />
          </Link>
          <Box sx={{ flexGrow: 1 }} />
          {isSmallScreen ? (
            <>
              <IconButton
                edge='start'
                color='inherit'
                aria-label='menu'
                onClick={toggleDrawer(true)}
                sx={{ marginRight: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer anchor='right' open={drawerOpen} onClose={toggleDrawer(false)}>
                <List>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleLinkClick(featuresHref)}>
                      <ListItemText primary='Features' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleLinkClick(subscriptionHref)}>
                      <ListItemText primary='Subscription Plans' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleLinkClick(contactHref)}>
                      <ListItemText primary='Contact' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => { navigate('/login'); toggleDrawer(false); }}>
                      <ListItemText primary='Login' />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Drawer>
            </>
          ) : (
            <Box
              sx={{
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: '8px', sm: '15px' },
              }}
            >
              {location.pathname === '/' ? (
                <a href={featuresHref}>
                  <CustomButton text='Features' href='' />
                </a>
              ) : (
                <Link to={featuresHref}>
                  <CustomButton text='Features' href='' />
                </Link>
              )}
              {location.pathname === '/' ? (
                <a href={subscriptionHref}>
                  <CustomButton text='Subscription Plans' href='' />
                </a>
              ) : (
                <Link to={subscriptionHref}>
                  <CustomButton text='Subscription Plans' href='' />
                </Link>
              )}
              {location.pathname === '/' ? (
                <a href={contactHref}>
                  <CustomButton text='Contact' href='' />
                </a>
              ) : (
                <Link to={contactHref}>
                  <CustomButton text='Contact' href='' />
                </Link>
              )}
              <Button
                variant='contained'
                onClick={() => navigate('/login')}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Login
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
};

export default Header;
